import firebase from 'firebase/app';
import 'firebase/analytics';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import fetch from 'node-fetch';
import Remote from '@/remote/Remote';
import User from '@/types/User';
import Project, {ProjectDef, ProjectFile, ProjectId} from '@/types/Project';
import SearchFilter, {SearchOrder} from '@/types/SearchFilter';
import Submission, {SubmissionDef, SubmissionId} from '@/types/Submission';
import Query = firebase.firestore.Query;
import DocumentData = firebase.firestore.DocumentData;
import DocumentSnapshot = firebase.firestore.DocumentSnapshot;
import Reference = firebase.storage.Reference;
import FieldValue = firebase.firestore.FieldValue;
import DocumentReference = firebase.firestore.DocumentReference;

const SUBMISSIONS_FOLDER = '0.submissions';

const firebaseConfig = {
    devl: {
        apiKey: 'AIzaSyD8BN9_MQkTzKscK7mgsEGGmdLPvbwxwLM',
        authDomain: 'retrolab-devl.firebaseapp.com',
        databaseURL: 'https://retrolab-devl.firebaseio.com',
        projectId: 'retrolab-devl',
        storageBucket: 'retrolab-devl.appspot.com',
        messagingSenderId: '515386083501',
        appId: '1:515386083501:web:1c5c08a7f3151f07e39e24',
        measurementId: 'G-6WKV7H3FHP',
    },
    test: {
        apiKey: 'AIzaSyArngA-46bAODKVG1AZUwh7MfUIyRIGK7Q',
        authDomain: 'retrolab-test.firebaseapp.com',
        databaseURL: 'https://retrolab-test.firebaseio.com',
        projectId: 'retrolab-test',
        storageBucket: 'retrolab-test.appspot.com',
        messagingSenderId: '764862204276',
        appId: '1:764862204276:web:9427073ceae15b9aac0421',
    },
    prod: {
        apiKey: 'AIzaSyCozHtHpmyUIq5WIaLfiuAp3ycwLtjo7lI',
        authDomain: 'retrolab-f5fb3.firebaseapp.com',
        databaseURL: 'https://retrolab-f5fb3.firebaseio.com',
        projectId: 'retrolab-f5fb3',
        storageBucket: 'retrolab-f5fb3.appspot.com',
        messagingSenderId: '139745568689',
        appId: '1:139745568689:web:1a7d642bd349714f3bff90',
        measurementId: 'G-4VDCPWTPPV',
    },
};

class RemoteFirebase extends Remote {
    //
    // files
    //

    private static async saveFileRef(ref: firebase.storage.Reference, content: FileContent): Promise<void> {
        if (typeof content === 'string') {
            await ref.putString(content, undefined, { contentType: 'text/x-asm' });
        } else if (Array.isArray(content)) {
            await ref.put(new Uint8Array(content));
        } else {
            await ref.put(content);
        }
    }

    private static async loadFile(fref: firebase.storage.Reference) {
        const url = await fref.getDownloadURL();
        const response = await fetch(url);
        if (response.ok) {
            const {contentType} = await fref.getMetadata();
            if (contentType.startsWith('text')) {
                return {name: fref.name, content: await response.text(), dirty: false} as ProjectFile;
            } else {
                return {
                    name: fref.name,
                    // @ts-ignore
                    content: new Uint8Array(await (await response.blob()).arrayBuffer()),
                    dirty: false,
                } as ProjectFile;
            }
        } else {
            throw new Error(`Error loading file '${fref.name}': ${response.statusText}`);
        }
    }

    //
    // instance properties / methods
    //

    private readonly env: Environment;
    private storage: firebase.storage.Reference;
    private db: firebase.firestore.Firestore;

    constructor(env: Environment) {
        super();
        if (!(env in firebaseConfig)) {
            throw new Error(`Env must be 'devl', 'test' or 'prod'.`);
        }
        this.env = env;
        firebase.initializeApp(firebaseConfig[env]);
        // if (process.env.JEST_WORKER_ID !== undefined)  // this fails on jest
        //   firebase.analytics()
        firebase.auth();
        this.storage = firebase.storage().ref();
        this.db = firebase.firestore();
    }

    //
    // users
    //

    public async login(): Promise<User> {
        try {
            const user = await firebase.auth().signInWithPopup(new firebase.auth.GoogleAuthProvider());
            return {
                uid: user.user!.uid,
                displayName: user.user!.displayName,
                email: user.user!.email,
                username: user.user!.email!.split('@')[0],
            } as User;
        } catch (e) {
            if (e.code === 'auth/popup-closed-by-user') {
                console.warn(e.message);
                return Promise.reject(e);
            } else {
                throw e;
            }
        }
    }

    public async logout(): Promise<void> {
        return firebase.auth().signOut();
    }

    public async createFakeUser(email: string): Promise<UID> {
        if (this.env === 'prod') {
            throw new Error(`Can't create a fake user in production.`);
        }
        const user = await firebase.auth().createUserWithEmailAndPassword(email, 'fakepass');
        return user.user!.uid;
    }

    public async deleteFakeUser(): Promise<void> {
        if (this.env === 'prod') {
            throw new Error(`Can't delete a fake user in production.`);
        }
        const user = firebase.auth().currentUser;
        if (user) {
            await Promise.all([
                this.db.collection('favorites').doc(user.uid).delete(),
                user!.delete(),
            ]);
        }
    }

    //
    // project list
    //

    public async userProjectList(uid: UID): Promise<ProjectDef[]> {
        const col = this.db.collection('projects');
        const list: ProjectDef[] = [];
        const query = await col.where('uid', '==', uid).get();
        query.forEach((doc) => list.push({ id: doc.id, project: doc.data() as Project }));
        return list;
    }

    //
    // project
    //

    public async createProject(project: Project): Promise<ProjectId> {
        const thisProject: Project = { ...project };
        if (thisProject.description === undefined) {
            delete thisProject.description;
        }
        const ref = await this.db.collection('projects').add(thisProject);
        return ref.id;
    }

    public async deleteProject(projectId: ProjectId): Promise<void> {
        const project = await this.loadProject(projectId);
        await this.deleteAllFiles(project.uid, projectId);
        const doc = await this.db.collection('projects').doc(projectId);
        return doc.delete();
    }

    public async loadProject(projectId: ProjectId): Promise<Project> {
        const doc = await this.db.collection('projects').doc(projectId).get();
        if (doc.exists) {
            return doc.data() as Project;
        } else {
            throw new Error('Document does not exist.');
        }
    }

    public async updateProject(project: ProjectDef): Promise<void> {
        const doc = await this.db.collection('projects').doc(project.id);
        const projectCopy: Project = { ...project.project };
        await doc.update(projectCopy);
    }

    public async saveFile(uid: UID, projectId: string, file: ProjectFile): Promise<void> {
        const ref = this.storage.child(`${uid}/${projectId}/${file.name}`);
        await RemoteFirebase.saveFileRef(ref, file.content);
    }

    public async deleteAllFiles(uid: UID, projectId: ProjectId): Promise<void[]> {
        const ref = this.storage.child(`${uid}/${projectId}`);
        const res = await ref.listAll();
        return Promise.all(res.items.map(async (fref) => fref.delete()));
    }

    public async updateFiles(uid: UID, projectId: ProjectId, filesToUpdate: ProjectFile[]): Promise<void[]> {
        return Promise.all(filesToUpdate.map(async (f) => {
            const ref = this.storage.child(`${uid}/${projectId}/${f.name}`);
            await RemoteFirebase.saveFileRef(ref, f.content);
        }));
    }

    public async listFiles(uid: UID, projectId: ProjectId): Promise<string[]> {
        const ref = this.storage.child(`${uid}/${projectId}`);
        const res = await ref.listAll();
        return res.items.map((fref) => fref.name);
    }

    public async deleteFiles(uid: UID, projectId: ProjectId, filesToDelete: string[]): Promise<void[]> {
        return Promise.all(filesToDelete.map(async (filename) => {
            const ref = this.storage.child(`${uid}/${projectId}/${filename}`);
            await ref.delete();
        }));
    }

    public async loadAllFiles(uid: UID, projectId: ProjectId): Promise<ProjectFile[]> {
        const ref = this.storage.child(`${uid}/${projectId}`);
        const res = await ref.listAll();
        return Promise.all(res.items.map(async (fref) => {
            return await RemoteFirebase.loadFile(fref);
        }));
    }

    //
    // project submission
    //

    public async submitProject(submission: Submission, binaryFile: FileContent, previousSubmissionId?: SubmissionId): Promise<SubmissionId> {
        if (submission.description === undefined) {
            delete submission.description;
        }
        if (previousSubmissionId) {
            const votes = ((await this.db.collection('submissions').doc(previousSubmissionId!).get()).data() as Submission).votes;
            await Promise.all([
                this.db.collection('submissions').doc(previousSubmissionId!).set({ ...submission, votes }),
                this.saveSubmissionBinary(previousSubmissionId!, binaryFile),
            ]);
            return previousSubmissionId!;
        } else {
            const ref = await this.db.collection('submissions').add(submission);
            await this.saveSubmissionBinary(ref.id as SubmissionId, binaryFile);
            return ref.id;
        }
    }

    public async loadSubmission(submissionId: SubmissionId): Promise<[Submission, FileContent]> {
        const [doc, binary]: [DocumentSnapshot<DocumentData>, FileContent] = await Promise.all([
            this.db.collection('submissions').doc(submissionId).get(),
            this.loadSubmissionBinary(submissionId),
        ]);
        if (doc.exists) {
            return [doc.data() as Submission, binary];
        } else {
            throw new Error('Document does not exist.');
        }
    }

    public async searchSubmissions(filter: SearchFilter): Promise<SubmissionDef[]> {
        if (filter.submissionId) {
            const doc = await this.db.collection('submissions').doc(filter.submissionId!).get();
            if (doc.exists) {
                return [{ id: filter.submissionId, submission: doc.data() } as SubmissionDef];
            } else {
                return [];
            }
        }
        let subRef: Query = await this.db.collection('submissions');
        if (filter.genre) {
            subRef = subRef.where('genres', 'array-contains', filter.genre!);
        }
        switch (filter.order) {
            case SearchOrder.New:
                subRef = subRef.orderBy('date', 'desc');
                break;
            case SearchOrder.Favorites:
                subRef = subRef.orderBy('votes', 'desc');
                break;
            default:
                throw new Error('No ordering defined for search');
        }
        const snapshot = await subRef.get();
        let submissions: SubmissionDef[] = [];
        snapshot.forEach((result) => submissions.push({ id: result.id, submission: result.data() } as SubmissionDef));
        if (filter.partialName) {
            submissions = submissions.filter((s) => s.submission.name.toLowerCase().includes(filter.partialName!.toLowerCase()));
        }
        if (filter.onlyFavoritesUid) {
            submissions = await this.filterByFavorites(submissions, filter.onlyFavoritesUid!);
        }
        return submissions;
    }

    public async deleteSubmission(submissionId: SubmissionId): Promise<void> {
        // find project id
        const doc = await this.db.collection('submissions').doc(submissionId);
        const submission = await doc.get();
        if (!submission.exists) {
            return;
        }
        const projectId = (submission.data() as Submission).projectId;

        await Promise.all([
            this.db.collection('projects').doc(projectId).set({ submissionId: FieldValue.delete() }, { merge: true }),
            this.deleteSubmissionBinary(submissionId),
            doc.delete(),
        ]);
    }

    public async upvoteSubmission(uid: UID, submissionId: SubmissionId): Promise<void> {
        if (await this.isFavorite(uid, submissionId)) {
            return;
        }
        const addFavorite = async () => {
            const doc = await this.ensureFavoriteCollection(uid);
            await doc.update({ submissions: FieldValue.arrayUnion(submissionId) });
        };
        await Promise.all([
            addFavorite(),
            this.db.collection('submissions').doc(submissionId).update({ votes: FieldValue.increment(1) }),
        ]);
    }

    public async downvoteSubmission(uid: UID, submissionId: SubmissionId): Promise<void> {
        if (!await this.isFavorite(uid, submissionId)) {
            return;
        }
        const removeFavorite = async () => {
            const doc = await this.ensureFavoriteCollection(uid);
            await doc.update({ submissions: FieldValue.arrayRemove(submissionId) });
        };
        await Promise.all([
            removeFavorite(),
            this.db.collection('submissions').doc(submissionId).update({ votes: FieldValue.increment(-1) }),
        ]);
    }

    public async listFavorites(uid: UID): Promise<SubmissionId[]> {
        const doc = await this.db.collection('favorites').doc(uid).get();
        if (!doc.exists) {
            return [];
        }
        return doc.data()!.submissions as SubmissionId[];
    }

    public async saveSubmissionBinary(submissionId: SubmissionId, content: FileContent): Promise<void> {
        return RemoteFirebase.saveFileRef(this.submissionFileRef(submissionId), content);
    }

    public async loadSubmissionBinary(submissionId: SubmissionId): Promise<FileContent> {
        return (await RemoteFirebase.loadFile(this.submissionFileRef(submissionId))).content;
    }

    public async deleteSubmissionBinary(submissionId: SubmissionId): Promise<void> {
        return this.submissionFileRef(submissionId).delete();
    }

    //
    // other
    //

    public async close(): Promise<void> {
        await this.db.terminate();
    }

    public debugState(): void {
        throw new Error('Method not implemented.');
    }

    public async ensureEmptyDatabase(): Promise<void> {
        const [projectCol, submissionCol, favoriteCol, lst] = await Promise.all([
            this.db.collection('projects').get(),
            this.db.collection('submissions').get(),
            this.db.collection('favorites').get(),
            this.storage.listAll(),
        ]);
        if (projectCol.size !== 0 || submissionCol.size !== 0 || favoriteCol.size !== 0 || lst.items.length !== 0) {
            console.error('Database is not empty.');
            process.exit(1);
        }
    }

    private async filterByFavorites(submissions: SubmissionDef[], uid: UID): Promise<SubmissionDef[]> {
        const doc = await this.db.collection('favorites').doc(uid).get();
        if (!doc.exists) {
            return [];
        }
        const favorites = doc.data()!.submissions as SubmissionId[];
        return submissions.filter((s) => favorites.includes(s.id));
    }

    private async ensureFavoriteCollection(uid: string): Promise<DocumentReference<DocumentData>> {
        const doc = this.db.collection('favorites').doc(uid);
        if (!(await doc.get()).exists) {
            await doc.set({submissions: []});
        }
        return doc;
    }

    private async isFavorite(uid: UID, submissionId: SubmissionId) {
        const fav = await this.db.collection('favorites').doc(uid).get();
        if (!fav.exists) {
            return false;
        }
        return (fav.data()!.submissions as SubmissionId[]).includes(submissionId);
    }

    // submission files

    private submissionFileRef(submissionId: SubmissionId): Reference {
        return this.storage.child(`${SUBMISSIONS_FOLDER}/${submissionId}.bin`);
    }
}

export default RemoteFirebase;
